import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import GeneralNotice from '@pelckmans/elementis/components/general-notice';
import List from '@pelckmans/elementis/components/list';
import SearchField from '@pelckmans/elementis/components/search-field';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrentOrPreviousGroup, Member, School } from '../../../../../api/groups';
import { getMedialinkById, Medialink } from '../../../../../api/medialink';
import { Module } from '../../../../../api/module';
import { NodeWithoutDescription } from '../../../../../api/table-of-content';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../constants/analyticsEvents';
import useAnalytics from '../../../../../hooks/useAnalytics';
import { useFuzzySearch } from '../../../../../hooks/useFuzzySearch';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import { getShowScore } from '../../../../../utils/results';
import { ScorableItem } from '../../api/scorable-items';
import { ResultWithGroup } from '../per-student/hooks/useResults';
import ExportAlerts from './components/Alerts';
import ExportFooter from './components/Footer';
import ExportHeader from './components/Header';
import ScoreIndicator from './score-rendering';
import { exportToSkore } from './skoreExport';

const TRANSLATION_BASE = 'PAGES.RESULTS.MODALS.SMARTSCHOOL_SKORE_EXPORT';

export type Status = 'idle' | 'active' | 'failed' | 'invalid';

type MemberWithGroup = Member & { groupId: string; groupName: string };

type CheckedMemberResult = { groupId: string; result: ResultWithGroup };

type Props = {
  id: string;
  members: Record<string, Member[]>;
  onSuccess: () => Promise<void>;
  results: ResultWithGroup[];
  scorableItems: ScorableItem[];
  selectedGroups: CurrentOrPreviousGroup[];
  selectedModule: Module;
  selectedSchool: School;
  selectedMembers: Record<string, Member[]>;
  toc: NodeWithoutDescription[];
};

function SkoreExportModal({
  id,
  members,
  onSuccess,
  results,
  scorableItems = [],
  selectedGroups,
  selectedModule,
  selectedSchool,
  selectedMembers,
  toc,
}: Props) {
  const { t } = useTranslation();
  const handleError = useThrowAsyncError();
  const analytics = useAnalytics();

  const [status, setStatus] = useState<Status>('idle');
  const [medialink, setMedialink] = useState<Medialink>();
  const [collapsedSchool, setCollapsedSchool] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({
    ...Object.fromEntries(selectedGroups.map(group => [group.id, false])),
  });
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<MemberWithGroup[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const hasPoints = scorableItems.every(item => item.showPoints);
  const hasMaterialAssignments = scorableItems.some(item => item.type === 'user-material');
  const scorableItem = scorableItems[0];
  const hasSyncedResults = results.some(result => result.isSmartschoolSynced);
  const showAlertGroup =
    hasSyncedResults || !hasPoints || hasMaterialAssignments || status === 'invalid' || status === 'failed';

  const [checkedState, setCheckedState] = useState(() => {
    const initial: Record<string, boolean> = {};
    selectedGroups.forEach(group => {
      members[group.id]?.forEach(member => {
        const isSelected = Boolean(selectedMembers[group.id]?.some(selectedMember => selectedMember.id === member.id));
        const result = results.find(
          r => r.memberId === member.id && r.groupId === group.id && r.isAssignment === scorableItem.isAssignment,
        );
        initial[`${group.id}-${member.id}`] = isSelected && Boolean(result?.achievedAt);
      });
    });
    return initial;
  });

  useEffect(() => {
    async function fetchMedialink() {
      if (scorableItem.type !== 'medialink') return;
      try {
        const ml = await getMedialinkById(scorableItem.module, scorableItem.id.split('-')[0]);
        setMedialink(ml);
      } catch (error) {
        handleError(error);
      }
    }
    fetchMedialink();
  }, [scorableItem, handleError]);

  const toggleMember = (id: string, value: boolean) => {
    setCheckedState(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async () => {
    const checkedMembersWithResults: CheckedMemberResult[] = selectedGroups.flatMap(group =>
      (members[group.id] ?? [])
        .filter(member => checkedState[`${group.id}-${member.id}`])
        .map((member): CheckedMemberResult | null => {
          const result = results.find(
            result =>
              result.groupId === group.id &&
              result.memberId === member.id &&
              result.isAssignment === scorableItem.isAssignment,
          );
          return result ? { groupId: group.id, result } : null;
        })
        .filter((entry): entry is CheckedMemberResult => entry !== null),
    );

    if (checkedMembersWithResults.length === 0) {
      setStatus('invalid');
      return;
    }
    setStatus('active');

    try {
      await exportToSkore({ checkedMembersWithResults, scorableItem, medialink, selectedModule });

      await analytics.capture({
        eventType: ANALYTICS_EVENT_TYPES.RESULTS_EXPORTED_SKORE,
        objectId: selectedModule.id,
        subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_RESULTS,
      });

      await onSuccess();
    } catch {
      setStatus('failed');
    }
  };

  const showScore = medialink && getShowScore(medialink);

  const allMembers: MemberWithGroup[] = selectedGroups.flatMap(group =>
    (members[group.id] ?? []).map(m => ({
      ...m,
      groupId: group.id,
      groupName: group.name,
    })),
  );

  const fuzzySearch = useFuzzySearch(
    allMembers.map(m => ({
      ...m,
      searchField: `${m.lastName} ${m.firstName} ${m.groupName}`,
    })),
    'searchField',
  );

  const filterList = () => {
    if (!searchValue.trim()) {
      clearSearch();
      return;
    }
    setIsSearching(true);
    setSearchResults(fuzzySearch(searchValue));
  };

  const clearSearch = () => {
    setSearchValue('');
    setSearchResults([]);
    setIsSearching(false);
  };

  useEffect(() => {
    if (isSearching && searchResults.length > 0) {
      setCollapsedSchool(false);

      const groupsWithResults = new Set(searchResults.map(member => member.groupId));
      const newCollapsedState: Record<string, boolean> = {};

      selectedGroups.forEach(group => {
        newCollapsedState[group.id] = !groupsWithResults.has(group.id);
      });

      setCollapsedGroups(newCollapsedState);
    }
  }, [isSearching, searchResults, selectedGroups]);

  const visibleMembers = isSearching ? searchResults : allMembers;

  return (
    <Dialog id={id} modal size="md" aria-label={t(`${TRANSLATION_BASE}.A11Y_LABEL`)}>
      <ExportHeader medialink={medialink} scorableItem={scorableItem} selectedModule={selectedModule} id={id} toc={toc}>
        <SearchField
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          onClear={clearSearch}
          onSearch={() => filterList()}
          aria-label={t(`${TRANSLATION_BASE}.HEADER.SEARCH_FIELD.A11Y_LABEL`)}
          clearActionLabel={t(`${TRANSLATION_BASE}.HEADER.SEARCH_FIELD.CLEAR_ACTION_LABEL`)}
          placeholder={t(`${TRANSLATION_BASE}.HEADER.SEARCH_FIELD.PLACEHOLDER`)}
          searchActionLabel={t(`${TRANSLATION_BASE}.HEADER.SEARCH_FIELD.SEARCH_ACTION_LABEL`)}
        />
      </ExportHeader>
      <Dialog.Body
        alertGroupProps={
          showAlertGroup
            ? {
                'aria-label': undefined,
                role: undefined,
                children: (
                  <ExportAlerts
                    status={status}
                    hasSyncedResults={hasSyncedResults}
                    hasNoPoints={!hasPoints}
                    hasUserMaterial={hasMaterialAssignments}
                    onSubmit={handleSubmit}
                  />
                ),
              }
            : undefined
        }
      >
        {isSearching && visibleMembers.length === 0 ? (
          <GeneralNotice
            text={t(`${TRANSLATION_BASE}.NO_CONTENT.TITLE`)}
            subText={t(`${TRANSLATION_BASE}.NO_CONTENT.TEXT`)}
            action={
              <Button prominence="neutral" size="sm" onClick={() => clearSearch()}>
                {t(`${TRANSLATION_BASE}.NO_CONTENT.ACTION`)}
              </Button>
            }
          />
        ) : (
          <List>
            {selectedSchool && (
              <List.Item
                collapsed={collapsedSchool}
                collapseButtonLabelText=""
                level={0}
                onToggleCollapse={() => setCollapsedSchool(prev => !prev)}
                title={selectedSchool.name}
              />
            )}
            {!collapsedSchool &&
              selectedGroups.map(group => {
                const visibleGroupMembers =
                  members[group.id]?.filter(m =>
                    visibleMembers.some(vm => vm.id === m.id && vm.groupId === group.id),
                  ) ?? [];

                if (visibleGroupMembers.length === 0) return null;

                return (
                  <Fragment key={group.id}>
                    <List.Item
                      as="checkbox"
                      collapseButtonLabelText={t(`${TRANSLATION_BASE}.LIST.A11Y_LABEL`)}
                      level={1}
                      title={group.name}
                      collapsed={collapsedGroups[group.id]}
                      checked={false}
                      disabled
                      indeterminate
                      onToggleCollapse={() => setCollapsedGroups(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
                    />
                    {!collapsedGroups[group.id] &&
                      visibleGroupMembers.map(member => {
                        const result = results.find(
                          r =>
                            r.memberId === member.id &&
                            r.groupId === group.id &&
                            r.isAssignment === scorableItem.isAssignment,
                        );

                        return (
                          <List.Item
                            key={`${group.id}-${member.id}`}
                            as="checkbox"
                            level={2}
                            title={`${member.lastName} ${member.firstName}`}
                            checked={!!checkedState[`${group.id}-${member.id}`]}
                            disabled={!result?.achievedAt || Boolean(result?.unachievable)}
                            onClick={() =>
                              toggleMember(`${group.id}-${member.id}`, !checkedState[`${group.id}-${member.id}`])
                            }
                            contentArea={result && <ScoreIndicator result={result} showScore={showScore} />}
                          />
                        );
                      })}
                  </Fragment>
                );
              })}
          </List>
        )}
      </Dialog.Body>

      <ExportFooter id={id} onClear={() => setCheckedState({})} onSubmit={handleSubmit} status={status} />
    </Dialog>
  );
}

export default SkoreExportModal;
